import React from "react";
import cn from "classnames";
import {Button} from "biggerui";
import {type FleetCard, type FleetMember, type FleetPlacement} from "./fleet";
import {TemplatePreview} from "./TemplatePreview";

type Props = {
  card: FleetCard;
  isBusy: boolean;
  onPutOnAir: (member: FleetMember) => void;
};

function sizeText(card: FleetCard): string {
  const physical = `${card.width}×${card.height}`;
  const nominal = card.dimensions.replace("x", "×");
  return nominal === physical ? `${physical} px` : `${nominal} (${physical} px)`;
}

/**
 * Can a new design take the place of the one on air without new crops? An
 * archived design has its own crops from its time on air.
 */
function readiness(card: FleetCard, placement: FleetPlacement): string | null {
  if (card.pool !== "candidate") return null;
  const onAir = placement.members.find((member) => member.isOnAir);
  if (!onAir || onAir.key === card.key) return null;
  return onAir.width === card.width && onAir.height === card.height
    ? `Same size as ${onAir.label} (${onAir.id}): it can borrow its crops (fallbackTransformId).`
    : `Other size than ${onAir.label} (${onAir.id}): it needs its own crops and cut backgrounds.`;
}

function Placement({
  card,
  placement,
  isBusy,
  onPutOnAir,
}: {
  card: FleetCard;
  placement: FleetPlacement;
  isBusy: boolean;
  onPutOnAir: (member: FleetMember) => void;
}) {
  const note = readiness(card, placement);
  return (
    <li className="admin-template-placement" data-slot={placement.slotKey}>
      <div className="admin-template-placement-page">
        <input
          type="checkbox"
          checked={placement.members.some((member) => member.key === card.key && member.isOnAir)}
          readOnly
          tabIndex={-1}
          aria-label={`${card.label} is on air on ${placement.page}`}
        />
        <span>{placement.page}</span>
        <span className="admin-template-placement-position">
          #{placement.position} of {placement.total}
        </span>
      </div>
      <div className="admin-template-placement-details">
        {placement.layouts} · {placement.products}
      </div>
      {placement.members.length > 1 && (
        <div className="admin-template-members" role="radiogroup" aria-label={`Designs of ${placement.page}`}>
          {placement.members.map((member) => (
            <label
              key={member.key}
              className={cn("admin-template-member", {
                "is-on-air": member.isOnAir,
                "is-this": member.key === card.key,
              })}
            >
              <input
                type="radio"
                name={`${card.key}-${placement.slotKey}`}
                checked={member.isOnAir}
                disabled={isBusy}
                onChange={() => onPutOnAir(member)}
              />
              <span>
                {member.label} <code>{member.id}</code>
                {member.isCodeDefault && <em> · code default</em>}
              </span>
            </label>
          ))}
        </div>
      )}
      {note && <p className="admin-template-readiness">{note}</p>}
    </li>
  );
}

/** One design: its preview, its size and its places on the pages. */
export function TemplateCard({card, isBusy, onPutOnAir}: Props) {
  const self = card.placements
    .flatMap((placement) => placement.members)
    .find((member) => member.key === card.key);
  if (!self) throw new Error(`Template card "${card.key}" is in none of its places`);
  return (
    <article className="admin-template-card" data-template-key={card.key} data-pool={card.pool}>
      <TemplatePreview definition={card.preview} />
      <header className="admin-template-header">
        <strong className="admin-template-label">{card.label}</strong>
        <code className="admin-template-id">{card.id}</code>
        <span className="admin-template-size">{sizeText(card)}</span>
        <span className="admin-template-key">key {card.key}</span>
      </header>
      <ul className="admin-template-placements">
        {card.placements.map((placement) => (
          <Placement
            key={placement.slotKey}
            card={card}
            placement={placement}
            isBusy={isBusy}
            onPutOnAir={onPutOnAir}
          />
        ))}
      </ul>
      {card.pool !== "on-air" && (
        <Button size="sm" disabled={isBusy} onClick={() => onPutOnAir(self)}>
          Put on air
        </Button>
      )}
    </article>
  );
}
